import React from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  IconButton,
  Stack,
  Divider,
  Collapse,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';
import { StatusChip } from './StatusChip';
import { StatChip } from './StatChip';

/**
 * @component EmailCard
 * @description Mobile-friendly card component for displaying email data in a card format
 * @param {Object} props
 * @param {Object} props.data - The email/campaign/scheduled data object
 * @param {boolean} props.isExpanded - Whether the card is expanded
 * @param {function} props.onToggleExpand - Handler for expand/collapse
 * @param {string} props.type - Type of card ('individual' | 'bulk' | 'campaign' | 'scheduled')
 * @param {React.ReactNode} [props.expandedContent] - Content to show when expanded
 * @param {React.ReactNode} [props.actionButton] - Optional action button (e.g., "View" button)
 * @param {function} [props.onActionClick] - Handler for action button click
 * @returns {JSX.Element}
 */
export function EmailCard({
  data,
  isExpanded,
  onToggleExpand,
  type,
  expandedContent,
  actionButton,
  onActionClick,
}) {
  const renderIndividualEmailCard = () => {
    const { contactName, emailAddress, subject, sendDate, status } = data;
    const isDelivered = status === 'Delivered' || status === 'Opened' || status === 'Clicked';
    const isOpened = status === 'Opened' || status === 'Clicked';
    const isClicked = status === 'Clicked';

    return (
      <>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight="600" noWrap>
              {contactName || 'Unknown'}
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              {emailAddress}
            </Typography>
          </Box>
          <StatusChip status={status} />
        </Box>

        <Typography variant="body1" fontWeight="500" sx={{ mb: 1, wordBreak: 'break-word' }}>
          {subject}
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {sendDate}
        </Typography>

        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {isDelivered ? (
              <CheckCircleIcon color="success" fontSize="small" />
            ) : (
              <HorizontalRuleIcon sx={{ color: 'grey.400' }} fontSize="small" />
            )}
            <Typography variant="caption" color="text.secondary">
              Delivered
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {isOpened ? (
              <CheckCircleIcon color="success" fontSize="small" />
            ) : (
              <HorizontalRuleIcon sx={{ color: 'grey.400' }} fontSize="small" />
            )}
            <Typography variant="caption" color="text.secondary">
              Opened
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {isClicked ? (
              <CheckCircleIcon color="success" fontSize="small" />
            ) : (
              <HorizontalRuleIcon sx={{ color: 'grey.400' }} fontSize="small" />
            )}
            <Typography variant="caption" color="text.secondary">
              Clicked
            </Typography>
          </Box>
        </Stack>
      </>
    );
  };

  const renderBulkEmailCard = () => {
    const { subject, campaignName, sendDate, attempted, delivered, uniqueOpens, uniqueClicks, status } = data;

    return (
      <>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle1" fontWeight="600" sx={{ flex: 1, mr: 1, wordBreak: 'break-word' }}>
            {subject}
          </Typography>
          <StatusChip status={status} type="bulk" />
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {campaignName || 'N/A'}
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {sendDate}
        </Typography>

        <Box sx={{ mb: 1 }}>
          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 0.5 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 60 }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.7rem' }}>
                Sent
              </Typography>
              <StatChip value={attempted} />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 60 }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.7rem' }}>
                Delivered
              </Typography>
              <StatChip value={delivered} />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 60 }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.7rem' }}>
                Opens
              </Typography>
              <StatChip value={uniqueOpens} />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 60 }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.7rem' }}>
                Clicks
              </Typography>
              <StatChip value={uniqueClicks} />
            </Box>
          </Stack>
        </Box>
      </>
    );
  };

  const renderCampaignCard = () => {
    const { campaignName, totalEmailsSent, delivered, uniqueOpens, uniqueClicks } = data;

    return (
      <>
        <Typography variant="subtitle1" fontWeight="600" sx={{ mb: 1.5, wordBreak: 'break-word' }}>
          {campaignName}
        </Typography>

        <Box sx={{ mb: 1 }}>
          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 0.5 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 60 }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.7rem' }}>
                Sent
              </Typography>
              <StatChip value={totalEmailsSent} />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 60 }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.7rem' }}>
                Delivered
              </Typography>
              <StatChip value={delivered} />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 60 }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.7rem' }}>
                Opens
              </Typography>
              <StatChip value={uniqueOpens} />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 60 }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.7rem' }}>
                Clicks
              </Typography>
              <StatChip value={uniqueClicks} />
            </Box>
          </Stack>
        </Box>
      </>
    );
  };

  const renderScheduledCard = () => {
    const { Subject, ListName, ScheduleDate, contact_count, Status, ListID } = data;
    const recipientCount = contact_count !== null && contact_count !== undefined 
      ? contact_count 
      : (ListID === 0 ? 1 : '—');

    return (
      <>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle1" fontWeight="600" sx={{ flex: 1, mr: 1, wordBreak: 'break-word' }}>
            {Subject}
          </Typography>
          <StatusChip status={Status} type="scheduled" />
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {ListName || 'N/A'}
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {ScheduleDate}
        </Typography>

        {typeof recipientCount === 'number' ? (
          <StatChip value={recipientCount} />
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {recipientCount}
          </Typography>
        )}
      </>
    );
  };

  const renderCardContent = () => {
    switch (type) {
      case 'individual':
        return renderIndividualEmailCard();
      case 'bulk':
        return renderBulkEmailCard();
      case 'campaign':
        return renderCampaignCard();
      case 'scheduled':
        return renderScheduledCard();
      default:
        return null;
    }
  };

  return (
    <Card
      sx={{
        mb: 2,
        '&:hover': {
          boxShadow: 3,
        },
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <IconButton
            aria-label={isExpanded ? 'Collapse' : 'Expand'}
            aria-expanded={isExpanded}
            size="small"
            onClick={onToggleExpand}
            sx={{ mt: -0.5, ml: -1 }}
          >
            {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            {renderCardContent()}

            {actionButton && (
              <Box sx={{ mt: 1.5 }}>
                {React.cloneElement(actionButton, {
                  onClick: onActionClick || actionButton.props.onClick,
                  fullWidth: true,
                  size: 'small',
                })}
              </Box>
            )}
          </Box>
        </Box>
      </CardContent>

      {expandedContent && (
        <>
          <Divider />
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <Box sx={{ p: 2, bgcolor: 'background.paper' }}>
              {expandedContent}
            </Box>
          </Collapse>
        </>
      )}
    </Card>
  );
}

